/** Withdraw a pending lifecycle request (any lifecycle manager may; the request records who did).
 *  A confirmation: nothing changes on the tenant. A request decided meanwhile (409
 *  `request_not_pending`) turns Withdraw off and re-reads the tab. */
import { Alert, Box, Button, CircularProgress, Typography } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { useEffect } from 'react';

import { lifecycleApi, lifecycleError, lifecycleErrorWords } from '@/api/tenantLifecycle';
import type { TenantRequest } from '@/api/tenantRequests';
import { AppDialog } from '@/components/AppDialog';
import { useSnackbar } from '@/components/Snackbar';

import { LifecycleRequestQuote } from './lifecycleFormat';
import { useLifecycleRefresh } from './useLifecycleRefresh';

interface Props {
  topId: number;
  /** The request to withdraw; null keeps the dialog closed. */
  request: TenantRequest | null;
  onClose: () => void;
}

export function WithdrawLifecycleDialog({ topId, request, onClose }: Props) {
  const snackbar = useSnackbar();
  const refresh = useLifecycleRefresh();
  const mut = useMutation({
    mutationFn: (id: number) => lifecycleApi.withdraw(topId, id),
    onSuccess: () => {
      snackbar.show('Request withdrawn.', 'success');
      refresh();
      onClose();
    },
    onError: (e) => {
      if (lifecycleError(e)?.code === 'request_not_pending') refresh();
    },
  });

  const { reset: resetMutation } = mut;
  useEffect(() => {
    if (request) resetMutation();
  }, [request, resetMutation]);

  const decidedMeanwhile = lifecycleError(mut.error)?.code === 'request_not_pending';

  return (
    <AppDialog
      open={request != null}
      onClose={onClose}
      title="Withdraw the request"
      actions={
        <>
          <Button onClick={onClose} disabled={mut.isPending}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={mut.isPending || decidedMeanwhile}
            onClick={() => request && mut.mutate(request.id)}
            startIcon={mut.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            Withdraw request
          </Button>
        </>
      }
    >
      {request && (
        <Box sx={{ display: 'grid', gap: 2, pt: 1 }}>
          <LifecycleRequestQuote request={request} />
          <Typography variant="body2">
            The request is closed without a decision; nothing changes on the tenant. A new request
            can be made afterwards.
          </Typography>
          {mut.error != null && <Alert severity="error">{lifecycleErrorWords(mut.error)}</Alert>}
        </Box>
      )}
    </AppDialog>
  );
}
